import * as RDM from '@radix-ui/react-dropdown-menu';
import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const DropdownMenu = RDM.Root;
export const DropdownMenuTrigger = RDM.Trigger;

export function DropdownMenuContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <RDM.Portal>
      <RDM.Content
        align="end"
        sideOffset={4}
        className={cn(
          'z-50 min-w-[10rem] rounded-lg border border-border bg-elevated p-1 shadow-lg',
          className,
        )}
      >
        {children}
      </RDM.Content>
    </RDM.Portal>
  );
}

/**
 * `hint` is one sentence under the label saying what the item does. It is the
 * item's accessible DESCRIPTION, not part of its name, so «Banear» is still
 * found and announced as «Banear».
 */
export function DropdownMenuItem({
  children,
  onSelect,
  danger,
  hint,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  hint?: string;
  /** Shown but not selectable; say why in `hint`. */
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <RDM.Item
      onSelect={onSelect}
      disabled={disabled}
      aria-labelledby={hint ? `${id}-label` : undefined}
      aria-describedby={hint ? `${id}-hint` : undefined}
      className={cn(
        'cursor-pointer select-none rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-graphite',
        danger ? 'text-red' : 'text-text-primary',
        hint && 'max-w-[18rem]',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      {hint ? (
        <>
          <span id={`${id}-label`} className="block">
            {children}
          </span>
          <span id={`${id}-hint`} className="block text-xs font-normal text-text-muted">
            {hint}
          </span>
        </>
      ) : (
        children
      )}
    </RDM.Item>
  );
}
